// Buyer-side workshop selection helpers (pure, unit-tested). The buy page keeps
// picks per tier (tierId → chosen workshop session ids) and this module turns
// them into the checkout body + tells the UI when the choice is complete.
//
// MVP simplification: one pick set per TIER, replicated across that tier's
// quantity (everyone on a "2× Single" order attends the same workshop). The
// backend accepts per-ticket picks, so per-attendee choice is a later add-on.
import type { FestivalSession, FestivalTier } from "./types";

export type WorkshopSlot = {
  slotId: string; // slotKey, or the session id for a standalone slot
  timeLabel: string | null;
  options: FestivalSession[]; // concurrent workshops in this slot (pick one)
};

// Group the event's WORKSHOP sessions into slots (concurrent options share a
// slotKey; a null slotKey is its own slot). Sorted by the sessions' sortOrder.
export function buildWorkshopSlots(sessions: FestivalSession[]): WorkshopSlot[] {
  const workshops = sessions
    .filter((s) => s.kind === "WORKSHOP")
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const order: string[] = [];
  const bySlot = new Map<string, FestivalSession[]>();
  for (const w of workshops) {
    const key = w.slotKey ?? w.id;
    if (!bySlot.has(key)) {
      bySlot.set(key, []);
      order.push(key);
    }
    bySlot.get(key)!.push(w);
  }
  return order.map((slotId) => ({
    slotId,
    timeLabel: bySlot.get(slotId)![0].timeLabel,
    options: bySlot.get(slotId)!,
  }));
}

// How many slots a tier's buyer attends: "all" = every slot, "pick" = quota.
export function tierSlotNeed(tier: Pick<FestivalTier, "workshopMode" | "workshopQuota">, slotCount: number): number {
  if (tier.workshopMode === "all") return slotCount;
  if (tier.workshopMode === "pick") return Math.max(1, tier.workshopQuota ?? 1);
  return 0;
}

// Which slot a chosen session belongs to.
function slotIdOf(sessionId: string, slots: WorkshopSlot[]): string | null {
  for (const s of slots) if (s.options.some((o) => o.id === sessionId)) return s.slotId;
  return null;
}

// A tier's pick is complete when it names `need` workshops in DISTINCT slots,
// all valid and not sold out. For an "all" tier every slot must be covered.
export function isTierPickComplete(
  tier: Pick<FestivalTier, "workshopMode" | "workshopQuota">,
  slots: WorkshopSlot[],
  chosen: string[],
): boolean {
  const need = tierSlotNeed(tier, slots.length);
  if (need === 0) return true;
  const distinctSlots = new Set<string>();
  for (const id of chosen) {
    const slot = slots.find((s) => s.options.some((o) => o.id === id && !o.soldOut));
    if (!slot) return false;
    distinctSlots.add(slot.slotId);
  }
  return distinctSlots.size === need && chosen.length === need;
}

// Auto-fill: an "all" tier whose every slot has a single (available) option
// needs no interaction, pre-select those. Returns the auto picks, or null when
// the buyer must choose (some slot has a real choice).
export function autoPicksForAll(slots: WorkshopSlot[]): string[] | null {
  if (slots.length === 0) return [];
  if (!slots.every((s) => s.options.length === 1)) return null;
  return slots.map((s) => s.options[0].id);
}

// Setting a slot's choice replaces any existing pick in that same slot.
export function setSlotChoice(current: string[], slots: WorkshopSlot[], sessionId: string): string[] {
  const slot = slotIdOf(sessionId, slots);
  const kept = current.filter((id) => slotIdOf(id, slots) !== slot);
  return [...kept, sessionId];
}

// Whether the buyer actually has a choice to make right now, a workshop tier
// is in the cart and it isn't a fully auto-fillable day pass. Drives showing the
// "pick your workshop" step.
export function workshopChoiceNeeded(
  tiers: FestivalTier[],
  slots: WorkshopSlot[],
  qty: Record<string, number>,
): boolean {
  const active = tiers.filter(
    (t) => (t.workshopMode === "all" || t.workshopMode === "pick") && (qty[t.id] ?? 0) > 0,
  );
  return active.some((t) => !(t.workshopMode === "all" && autoPicksForAll(slots) !== null));
}

// Build the checkout `workshops` array: one entry per ticket of each workshop
// tier, carrying that tier's chosen session ids. `complete` is false when any
// required pick is missing (the buy button stays disabled).
export function buildCheckoutWorkshops(
  tiers: FestivalTier[],
  slots: WorkshopSlot[],
  qty: Record<string, number>,
  picks: Record<string, string[]>,
): { workshops: Array<{ tierId: string; sessionIds: string[] }>; complete: boolean } {
  const workshops: Array<{ tierId: string; sessionIds: string[] }> = [];
  let complete = true;
  for (const tier of tiers) {
    if (tier.workshopMode !== "all" && tier.workshopMode !== "pick") continue;
    const n = qty[tier.id] ?? 0;
    if (n <= 0) continue;
    let chosen = picks[tier.id] ?? [];
    if (chosen.length === 0 && tier.workshopMode === "all") {
      chosen = autoPicksForAll(slots) ?? [];
    }
    if (!isTierPickComplete(tier, slots, chosen)) {
      complete = false;
      continue;
    }
    for (let i = 0; i < n; i++) workshops.push({ tierId: tier.id, sessionIds: chosen });
  }
  return { workshops, complete };
}
